import {
	type Form360Data,
	buildForm360Schema,
	buildStandardFormSchema,
	externalFormSchema,
} from "../../../../../../components/FormSelectionCard";

import type { AddFormsStepData, ConfiguredForm, FormType } from "./types";

export type ValidationFailure =
	| { kind: "no-template" }
	| { kind: "invalid-form"; formType: FormType; localId: string }
	| { kind: "duplicate-360"; formType: "360"; localId: string; formName: string };

/** A 360 already on the cycle, reduced to the fields that define a duplicate. */
export type Existing360Selection = {
	formId: string;
	whoCanAddForm: Form360Data["whoCanAddForm"];
	formAuthor?: Form360Data["formAuthor"];
};

export type ValidationResult = { valid: true } | { valid: false; failures: ValidationFailure[] };

const buildSchemaByType = (minDueDate?: Date) =>
	({
		standard: buildStandardFormSchema(minDueDate),
		"360": buildForm360Schema(minDueDate),
		external: externalFormSchema,
	}) as const;

const collectInvalidFormFailures = (forms: AddFormsStepData["forms"], minDueDate?: Date): ValidationFailure[] => {
	const failures: ValidationFailure[] = [];
	const schemaByType = buildSchemaByType(minDueDate);

	for (const formType of ["standard", "360", "external"] as const) {
		for (const form of forms[formType]) {
			const schema = schemaByType[formType];
			const result = schema.safeParse(form.data);
			if (!result.success) {
				failures.push({ kind: "invalid-form", formType, localId: form.localId });
			}
		}
	}

	return failures;
};

export const validateAddForms = (data: AddFormsStepData | undefined, cycleStartDate?: Date): ValidationResult => {
	const stepData: AddFormsStepData = data ?? {
		selectedTemplate: null,
		forms: { standard: [], "360": [], external: [] },
	};

	const failures: ValidationFailure[] = [];

	if (!stepData.selectedTemplate) {
		failures.push({ kind: "no-template" });
	}

	failures.push(...collectInvalidFormFailures(stepData.forms, cycleStartDate));

	return failures.length === 0 ? { valid: true } : { valid: false, failures };
};

// The author-type designation only disambiguates when the subject picks the author
const duplicate360Key = ({ formId, whoCanAddForm, formAuthor }: Existing360Selection): string =>
	[formId, whoCanAddForm, whoCanAddForm === "Subject" ? (formAuthor ?? "") : ""].join("|");

const collectDuplicate360Failures = (
	forms: ConfiguredForm<"360">[],
	existing360Selections: Existing360Selection[]
): ValidationFailure[] => {
	const seenKeys = new Set(existing360Selections.map(duplicate360Key));
	const failures: ValidationFailure[] = [];

	for (const form of forms) {
		const key = duplicate360Key({
			formId: form.formId,
			whoCanAddForm: form.data.whoCanAddForm,
			formAuthor: form.data.formAuthor,
		});
		if (seenKeys.has(key)) {
			failures.push({
				kind: "duplicate-360",
				formType: "360",
				localId: form.localId,
				formName: form.data.formName,
			});
		}
		seenKeys.add(key);
	}

	return failures;
};

/**
 * Validates ad-hoc forms only — no template requirement (used outside the wizard).
 * Pass `existing360Selections` (the cycle's active configs, may be empty) to also
 * reject 360s duplicating an existing config or another card in the batch.
 * Pass `minDueDate` (the effective cycle start date) to reject fixed due dates
 * before it.
 */
export const validateAdHocForms = (
	forms: AddFormsStepData["forms"],
	existing360Selections?: Existing360Selection[],
	minDueDate?: Date
): ValidationResult => {
	const failures = collectInvalidFormFailures(forms, minDueDate);

	if (existing360Selections) {
		failures.push(...collectDuplicate360Failures(forms["360"], existing360Selections));
	}

	return failures.length === 0 ? { valid: true } : { valid: false, failures };
};
